import React, { Fragment } from "react";
import { FlatListItem } from "../../../components/partials/FlatList";
import { Result } from "../../../models/result.model";
import { StartListEntry } from "../../../models/startlist.model";
import { Test } from "../../../models/test.model";
import { markToDouble } from "../../../tools";
import { AnimatedFlatList } from "./components/AnimatedFlatList";
import { Mark } from "./components/JudgeMark";
import { LowerThird } from "./components/LowerThird";
import { Flag } from "./components/Flag";
import { SquareList } from "./components/SquareList";
import {
  SecondaryNameColumn,
  ResultColumn,
  TeamColumn,
} from "./components/Column";
import { useIsBreeding } from "./hooks/useIsBreeding";
import { useBackgroundColor } from "./hooks/useBackgroundColor";

const EquipageResult: React.FC<FlatListItem<Result, Test>> = ({
  item: result,
  onHidden,
  show,
  extraData: test,
}) => {
  const marks = result.marks?.map((mark) => (
    <Mark
      key={`${result.id}.${mark.judgeNo}`}
      mark={mark}
      test={test}
      result={result}
    />
  ));

  const finalMark = (
    <>
      <div style={{ backgroundColor: "var(--blue)" }}>
        <span className="light mr-2">Rank</span>
        <span>{result.rank}</span>
      </div>
      <div>
        <span className="light mr-2">Total</span>
        <span>{markToDouble(result.mark, test.roundingPrecision)}</span>
      </div>
    </>
  );

  return (
    <LowerThird
      header={
        <>
          {result.rider?.fullname}
          <Flag
            countryCode={result.rider?.team ?? ""}
            style={{
              height: "1em",
              marginLeft: "auto",
            }}
          />
        </>
      }
      color={result.color}
      onHidden={onHidden}
      show={show}
      className="flatlist-item"
      footer={finalMark}
    >
      {marks}
    </LowerThird>
  );
};

const EquipageInfo: React.FC<FlatListItem<StartListEntry, Test>> = ({
  item,
  onHidden,
  show,
}) => {
  return (
    <LowerThird
      header={
        <>
          {item.rider?.fullname}
          <Flag
            countryCode={item.rider?.team ?? ""}
            style={{
              height: "1em",
              marginLeft: "auto",
            }}
          />
        </>
      }
      color={item.color}
      onHidden={onHidden}
      show={show}
      className="flatlist-item"
      gridTemplateColumns="2fr min-content"
    >
      <div>
        <span className="mr-4">{item.horse?.horseName}</span>
        <span className="light">{item.horse?.feifId}</span>
      </div>
      <div>
        <span>{item.rider?.ageGroup}</span>
      </div>
    </LowerThird>
  );
};

const SquareEquipageInfo: React.FC<{ group: StartListEntry[]; test: Test }> = ({
  group,
  test,
}) => {
  const isBreeding = useIsBreeding(test);
  return (
    <>
      {group?.map((result) => (
        <SquareList style={{ gridArea: "listarea" }}>
          <header>
            <div
              className="color"
              style={{
                backgroundColor: `var(--${
                  result.color !== "" ? result.color : "blue"
                })`,
              }}
            ></div>
            <h1>
              {isBreeding ? result.horse?.horseName : result?.rider?.fullname}
            </h1>
            <TeamColumn
              team={result?.rider?.team ?? ""}
              style={{ marginLeft: "auto" }}
            />
          </header>
          <main data-show="true">
            <div className="additional-info">
              <SecondaryNameColumn test={test} entry={result} showFeifId />
              <div>{result.rider?.ageGroup}</div>
            </div>
          </main>
        </SquareList>
      ))}
    </>
  );
};

const SquareEquipageResult: React.FC<{ group: Result[]; test: Test }> = ({
  group,
  test,
}) => {
  const isBreeding = useIsBreeding(test); // only show section marks if it's a breeding test

  return (
    <>
      {group?.map((result) => {
        const sectionMarks =
          test.sections?.map((section) => {
            return {
              sectionName: section.sectionName,
              marks:
                result.marks?.flatMap(
                  (mark) =>
                    mark.sectionMarks?.filter(
                      (sm) =>
                        sm.sectionNo === section.sectionNo &&
                        section.sectionName !== "Performance"
                    ) ?? []
                ) ?? [],
            };
          }) ?? [];

        console.log(sectionMarks);

        const colCount = sectionMarks[0]?.marks.length ?? 1;

        return (
          <SquareList
            key={result.id}
            style={{
              gridArea: "listarea",
            }}
          >
            <header>
              <div
                className="color"
                style={{
                  backgroundColor: `var(--${
                    result.color !== "" ? result.color : "blue"
                  })`,
                }}
              ></div>
              <h1>
                {isBreeding ? result.horse?.horseName : result?.rider?.fullname}
              </h1>
              <TeamColumn
                team={result.rider?.team ?? ""}
                style={{ marginLeft: "auto" }}
              />
            </header>
            <main data-show="true">
              {isBreeding && (
                <div
                  className="bs-table"
                  style={{
                    gridTemplateColumns: `min-content repeat(${colCount}, 1fr)`,
                  }}
                >
                  {sectionMarks.map((sm, i) => (
                    <Fragment key={result.id + sm.sectionName}>
                      <div data-last={i === sectionMarks.length - 1}>
                        {sm.sectionName}
                      </div>

                      {sm.marks.map((m) => (
                        <div
                          data-last={i === sectionMarks.length - 1}
                          style={{ textAlign: "right" }}
                        >
                          {markToDouble(m.mark, test.roundingPrecision ?? 2)}
                        </div>
                      ))}
                    </Fragment>
                  ))}
                </div>
              )}
              {!isBreeding && result.marks && (
                <div className="marklist">
                  {result?.marks
                    ?.filter((mark) => mark.markType !== "flag")
                    .map((mark) => (
                      <Mark
                        key={`${result.id}.${mark.judgeNo}`}
                        mark={mark}
                        test={test}
                        result={result}
                      />
                    ))}
                </div>
              )}
              <div className="additional-info">
                <dl>
                  <dt>Rank</dt>
                  <dd>{result.state === "VALID" ? result.rank : "-"}</dd>
                </dl>
                {result.scope && (
                  <dl>
                    <dt>Scope</dt>
                    <dd>{result.state === "VALID" ? result.scope : "-"}</dd>
                  </dl>
                )}
                <dl>
                  <dt>Total</dt>
                  <dd>
                    <ResultColumn result={result} test={test} />
                  </dd>
                </dl>
              </div>
            </main>
          </SquareList>
        );
      })}
    </>
  );
};

type CurrentEquipageProps = {
  variant?: "classic" | "square";
  type: "info" | "result";
  currentGroup: StartListEntry[] | Result[];
  test: Test;
};

export const CurrentEquipage: React.FC<CurrentEquipageProps> = ({
  variant = "classic",
  type,
  currentGroup,
  test,
}) => {
  switch (variant) {
    case "square":
      if (type === "info") {
        return (
          <SquareEquipageInfo
            group={currentGroup as StartListEntry[]}
            test={test}
          />
        );
      }
      if (type === "result")
        return (
          <SquareEquipageResult group={currentGroup as Result[]} test={test} />
        );
      return null;

    default:
      const renderTemplate = type === "info" ? EquipageInfo : EquipageResult;

      return (
        <>
          <AnimatedFlatList
            extraData={test}
            items={currentGroup}
            RenderComponent={renderTemplate}
            itemsPerPage={1}
            timePerPage={10000}
            usePlaceholder={false}
          />
        </>
      );
  }
};
